import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { guestNavigationItems } from '../config/navigation'
import { useTheme } from '../contexts/ThemeContext'
import Logo from './common/Logo'
import NavigationMenu from './common/NavigationMenu'
import ThemeToggle from './common/ThemeToggle'
import AuthButtons from './common/AuthButtons'
import MobileMenu from './common/MobileMenu'
import { trainingGroupsAPI } from '../config/api'
import 'primeicons/primeicons.css'

interface Announcement {
  id: number
  title: string
  content: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  is_pinned: boolean
  training_group?: { id: number; name: string } | null
  comment_count: number
  created_at: string
}

export default function HeaderGuest() {
  const location = useLocation()
  const pathname = location.pathname
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isNotiOpen, setIsNotiOpen] = useState(false)
  const [notifications, setNotifications] = useState<Announcement[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [readIds, setReadIds] = useState<Set<number>>(new Set())
  const notiRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsNotiOpen(false)
  }, [pathname])

  // Close on click outside (desktop)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notiRef.current && !notiRef.current.contains(e.target as Node)) {
        setIsNotiOpen(false)
      }
    }
    if (isNotiOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isNotiOpen])

  // Fetch public announcements
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await trainingGroupsAPI.getAnnouncements()
        const arr: Announcement[] = Array.isArray(data) ? data : (data?.results || [])
        setNotifications(arr)
        const stored = localStorage.getItem('saigonfc_read_noti')
        const storedSet: Set<number> = stored ? new Set(JSON.parse(stored)) : new Set()
        setReadIds(storedSet)
        setUnreadCount(arr.filter(n => !storedSet.has(n.id)).length)
      } catch {
        setNotifications([])
      }
    }
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 60000)
    return () => clearInterval(interval)
  }, [])

  const markAllRead = () => {
    const allIds = new Set(notifications.map(n => n.id))
    setReadIds(allIds)
    setUnreadCount(0)
    localStorage.setItem('saigonfc_read_noti', JSON.stringify([...allIds]))
  }

  const markOneRead = (id: number) => {
    const newSet = new Set(readIds)
    newSet.add(id)
    setReadIds(newSet)
    setUnreadCount(notifications.filter(n => !newSet.has(n.id)).length)
    localStorage.setItem('saigonfc_read_noti', JSON.stringify([...newSet]))
  }

  const priorityColor = (p: string) => {
    switch (p) {
      case 'urgent': return 'text-red-400 bg-red-500/10 border-red-500/30'
      case 'high':   return 'text-orange-400 bg-orange-500/10 border-orange-500/30'
      case 'normal': return 'text-gold-400 bg-gold-500/10 border-gold-500/30'
      default:       return 'text-gray-400 bg-gray-500/10 border-gray-500/30'
    }
  }

  const priorityLabel = (p: string) => {
    switch (p) {
      case 'urgent': return 'Khẩn cấp'
      case 'high':   return 'Cao'
      case 'normal': return 'Bình thường'
      default:       return 'Thấp'
    }
  }

  const formatTimeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Vừa xong'
    if (mins < 60) return `${mins} phút trước`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs} giờ trước`
    return `${Math.floor(hrs / 24)} ngày trước`
  }

  // Shared notification list renderer
  const NotificationList = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {notifications.length === 0 ? (
        <div className={`flex flex-col items-center justify-center ${mobile ? 'py-20' : 'py-12'} gap-3`}>
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100'}`}>
            <i className="pi pi-bell text-3xl text-gold-400"></i>
          </div>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Không có thông báo nào</p>
        </div>
      ) : (
        notifications.map(noti => {
          const isRead = readIds.has(noti.id)
          return (
            <div
              key={noti.id}
              onClick={() => markOneRead(noti.id)}
              className={`relative flex gap-3 ${mobile ? 'py-3.5 px-4 rounded-lg border-l-4' : 'px-4 py-3.5 border-b'} cursor-pointer transition-all ${
                mobile
                  ? !isRead
                    ? theme === 'dark' ? 'border-gold-400 bg-gold-900/20 hover:bg-gold-900/30' : 'border-gold-500 bg-gold-400/15 hover:bg-gold-400/25'
                    : theme === 'dark' ? 'border-transparent hover:border-gold-500 hover:bg-gold-900/20' : 'border-transparent hover:border-gold-400 hover:bg-gold-400/10'
                  : `${theme === 'dark' ? 'border-white/5 hover:bg-white/5' : 'border-gray-100 hover:bg-gray-50'} ${!isRead ? (theme === 'dark' ? 'bg-gold-500/5' : 'bg-gold-50/60') : ''}`
              }`}
            >
              {!mobile && !isRead && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gold-400"></span>
              )}
              <div className={`${mobile ? 'w-10 h-10' : 'w-9 h-9'} rounded-full flex-shrink-0 flex items-center justify-center border ${priorityColor(noti.priority)}`}>
                {noti.is_pinned ? <i className="pi pi-thumbtack text-sm"></i>
                  : noti.priority === 'urgent' ? <i className="pi pi-exclamation-triangle text-sm"></i>
                  : noti.priority === 'high'   ? <i className="pi pi-info-circle text-sm"></i>
                  : <i className="pi pi-bell text-sm"></i>}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`${mobile ? 'text-base' : 'text-sm'} leading-snug mb-0.5 ${theme === 'dark' ? 'text-white' : 'text-gray-900'} ${!isRead ? 'font-bold' : 'font-semibold'}`}>
                  {noti.is_pinned && <span className="mr-1">📌</span>}
                  {noti.title}
                </p>
                <p className={`${mobile ? 'text-sm mb-2' : 'text-xs line-clamp-2 mb-1'} leading-relaxed ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {noti.content}
                </p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`${mobile ? 'text-xs' : 'text-[10px]'} ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    {formatTimeAgo(noti.created_at)}
                  </span>
                  <span className={`${mobile ? 'text-xs px-2 py-0.5' : 'text-[10px] px-1.5 py-0.5'} rounded-full border ${priorityColor(noti.priority)}`}>
                    {priorityLabel(noti.priority)}
                  </span>
                </div>
              </div>
              {mobile && !isRead && <span className="w-2 h-2 rounded-full bg-gold-400 flex-shrink-0 mt-1"></span>}
            </div>
          )
        })
      )}
    </>
  )

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${theme === 'dark'
        ? isScrolled
          ? 'bg-black/95 backdrop-blur-md border-b-2 border-gold-500 shadow-lg shadow-gold-500/20'
          : 'bg-black border-b-2 border-gold-500'
        : isScrolled
          ? 'bg-gray-200/95 backdrop-blur-md border-b-2 border-gold-500 shadow-lg shadow-gold-500/20'
          : 'bg-gray-200 border-b-2 border-gold-500'
        }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Logo size={isScrolled ? 'small' : 'normal'} />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-1 justify-end max-w-[60%]">
            <NavigationMenu items={guestNavigationItems} variant="desktop" />
            <ThemeToggle variant="desktop" />

            {/* 🔔 Desktop Notification Bell */}
            <div className="relative" ref={notiRef}>
              <button
                onClick={() => setIsNotiOpen(v => !v)}
                className={`relative w-10 h-10 flex items-center justify-center transition-all duration-300 rounded-lg active:scale-95 ${
                  theme === 'dark' ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20' : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
                }`}
                aria-label="Thông báo"
              >
                <i className="pi pi-bell text-lg"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Desktop Dropdown */}
              {isNotiOpen && (
                <div className={`absolute right-0 mt-2 w-96 max-h-[520px] rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-950 border border-gold-500/30' : 'bg-white border border-gold-400/30'
                }`}>
                  {/* Header */}
                  <div className={`flex items-center justify-between px-4 py-3 border-b ${theme === 'dark' ? 'border-gold-500/20' : 'border-gold-400/20'}`}>
                    <div className="flex items-center gap-2">
                      <i className="pi pi-bell text-gold-400 text-lg"></i>
                      <span className={`font-bold text-base ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Thông báo</span>
                      {unreadCount > 0 && <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount} mới</span>}
                    </div>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead} className={`text-xs font-semibold transition ${theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'}`}>
                        Đánh dấu đã đọc
                      </button>
                    )}
                  </div>
                  {/* List */}
                  <div className="flex-1 overflow-y-auto">
                    <NotificationList />
                  </div>
                  {/* Footer */}
                  <div className={`px-4 py-2.5 border-t text-center ${theme === 'dark' ? 'border-gold-500/20' : 'border-gold-400/20'}`}>
                    <Link to="/login" onClick={() => setIsNotiOpen(false)} className={`text-xs font-semibold transition ${theme === 'dark' ? 'text-gold-400 hover:text-gold-300' : 'text-gold-600 hover:text-gold-700'}`}>
                      Đăng nhập để xem thêm →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <AuthButtons variant="desktop" />
          </div>

          {/* Mobile: Bell + Hamburger */}
          <div className="lg:hidden flex items-center gap-1">
            {/* 🔔 Mobile Bell */}
            <button
              onClick={() => setIsNotiOpen(v => !v)}
              className={`relative w-10 h-10 flex items-center justify-center transition-all duration-300 rounded-lg active:scale-95 ${
                theme === 'dark' ? 'text-gold-400 hover:text-gold-300 hover:bg-gold-900/20' : 'text-gray-700 hover:text-gray-900 hover:bg-gold-400/20'
              }`}
              aria-label="Thông báo"
            >
              <i className="pi pi-bell text-lg"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {/* Mobile Notification Panel */}
            {isNotiOpen && (
              <>
                <div
                  className={`fixed top-16 inset-x-0 bottom-0 z-[44] ${theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/50'}`}
                  onClick={() => setIsNotiOpen(false)}
                />
                <nav className={`fixed top-16 left-0 right-0 max-h-[70vh] overflow-y-auto border-b-2 border-gold-500 shadow-2xl z-[45] ${
                  theme === 'dark' ? 'bg-black' : 'bg-gray-200'
                }`}>
                  <div className="container mx-auto px-4 py-4">
                    {/* Panel Header */}
                    <div className="flex items-center justify-between h-14 mb-4 pb-3 border-b border-gold-500/30">
                      <div className="flex items-center gap-2">
                        <i className="pi pi-bell text-gold-400 text-xl"></i>
                        <span className={`font-bold text-lg ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>Thông báo</span>
                        {unreadCount > 0 && <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount} mới</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition ${theme === 'dark' ? 'border-gold-500/40 text-gold-400 hover:bg-gold-900/20' : 'border-gold-400/40 text-gold-600 hover:bg-gold-50'}`}>
                            Đã đọc
                          </button>
                        )}
                        <button onClick={() => setIsNotiOpen(false)} className={`w-9 h-9 flex items-center justify-center rounded-lg transition ${theme === 'dark' ? 'text-gold-400 hover:bg-gold-900/20' : 'text-gray-700 hover:bg-gold-400/20'}`}>
                          <span className="text-xl font-bold">×</span>
                        </button>
                      </div>
                    </div>
                    {/* List */}
                    <div className="flex flex-col gap-2 pb-4">
                      <NotificationList mobile />
                      <div className="flex items-center justify-center pt-3 mt-2 border-t border-gold-500/30">
                        <Link
                          to="/login"
                          onClick={() => setIsNotiOpen(false)}
                          className={`w-full py-3.5 px-4 flex items-center justify-center gap-2.5 font-semibold text-base transition-all rounded-lg border-2 border-gold-500/40 ${
                            theme === 'dark' ? 'text-gold-400 hover:bg-gold-900/20' : 'text-gray-800 hover:bg-gold-400/20'
                          }`}
                        >
                          <i className="pi pi-sign-in text-lg"></i>
                          <span>Đăng nhập để xem thêm</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </nav>
              </>
            )}

            {/* Hamburger Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="relative w-10 h-10 flex items-center justify-center text-gold-400 hover:text-gold-300 transition-colors duration-300 rounded-lg hover:bg-gold-900/20 active:scale-95"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </header>
  )
}
